import { useEffect, useMemo, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

export type SearchableSelectOption = {
  value: string
  label: string
  keywords?: string
  disabled?: boolean
}

type SearchableSelectProps = {
  value?: string
  onValueChange: (value: string) => void
  onSearchChange?: (value: string) => void
  onOpenChange?: (open: boolean) => void
  options: SearchableSelectOption[]
  placeholder: string
  searchPlaceholder?: string
  emptyText?: string
  emptySearchText?: string
  showAllOnEmptySearch?: boolean
  filterLocally?: boolean
  disabled?: boolean
  id?: string
  ariaLabel?: string
  triggerClassName?: string
  contentClassName?: string
}

export function SearchableSelect({
  value,
  onValueChange,
  onSearchChange,
  onOpenChange,
  options,
  placeholder,
  searchPlaceholder = 'Search...',
  emptyText = 'No matches found.',
  emptySearchText = 'Type to search.',
  showAllOnEmptySearch = true,
  filterLocally = true,
  disabled = false,
  id,
  ariaLabel,
  triggerClassName,
  contentClassName,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  )

  const filteredOptions = useMemo(() => {
    if (!filterLocally) {
      return options
    }

    const query = search.trim().toLowerCase()
    if (!query) {
      return showAllOnEmptySearch ? options : []
    }

    return options.filter((option) => {
      const haystack = `${option.label} ${option.keywords || ''} ${option.value}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [filterLocally, options, search, showAllOnEmptySearch])

  useEffect(() => {
    if (!open) {
      setSearch('')
      return
    }

    onSearchChange?.(search)
  }, [open, onSearchChange, search])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel || placeholder}
          disabled={disabled}
          className={cn(
            'h-auto min-h-9 w-full justify-between px-3 py-2 text-left font-normal',
            !selectedOption && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate">{selectedOption?.label || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-[min(48rem,var(--radix-popover-content-available-width))] max-w-[calc(100vw-1rem)] max-h-[min(24rem,var(--radix-popover-content-available-height))] overflow-hidden p-0',
          contentClassName,
        )}
      >
        <div className="border-b border-border p-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
        </div>
        <ScrollArea className="h-[min(18rem,calc(var(--radix-popover-content-available-height)-3.5rem))] max-h-72">
          <div className="p-2">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === value

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50',
                      isSelected && 'bg-muted',
                    )}
                    onClick={() => {
                      onValueChange(option.value)
                      setOpen(false)
                    }}
                  >
                    <Check className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                    <span className="min-w-0 whitespace-normal break-words leading-snug">{option.label}</span>
                  </button>
                )
              })
            ) : (
              <p className="px-3 py-6 text-sm text-muted-foreground">
                {search.trim() ? emptyText : emptySearchText}
              </p>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}